/**
 * Anivaze v4 — client for the tagged AI-image asset library.
 *
 * Asks the worker to pick-or-generate an asset (server runs the grow-and-mix
 * policy: a fresh variant per ~5 uses, random pick across the pool so old and
 * new variants both appear), then inlines the image as a data URI so the
 * deterministic SVG renderer — and the canvas exporter — can embed it without
 * CORS taint.
 */

const API_BASE: string =
  (import.meta.env.VITE_ORCHESTRATOR_BASE_URL as string | undefined) ||
  "https://learnengine-orchestrator-api.obedpaul-p.workers.dev";
const USER_ID: string = (import.meta.env.VITE_USER_ID as string | undefined) || "anonymous";

export interface PickedAsset {
  assetId: string;
  dataUri: string;
  reused: boolean;
}

export async function pickAsset(
  tag: string,
  kind: "sticker" | "scene",
  subject?: string,
  pack = "default",
  signal?: AbortSignal,
): Promise<PickedAsset> {
  const res = await fetch(`${API_BASE}/assets/pick`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-user-id": USER_ID },
    body: JSON.stringify({ tag, kind, subject, pack }),
    signal,
  });
  if (!res.ok) throw new Error(`asset pick failed: ${res.status}`);
  const data: any = await res.json();
  if (!data?.ok || !data.url) throw new Error("asset pick returned no url");

  const img = await fetch(`${API_BASE}${data.url}`, { signal });
  if (!img.ok) throw new Error(`asset fetch failed: ${img.status}`);
  const blob = await img.blob();
  const dataUri = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error("asset decode failed"));
    fr.readAsDataURL(blob);
  });
  return { assetId: data.assetId, dataUri, reused: !!data.reused };
}
